/**
 * distanceRequestBatcher.ts
 *
 * Batches multiple distance calculation API requests to reduce the number of calls.
 * Queues requests that come in close together and processes them in optimized batches.
 *
 * This reduces API calls by:
 * - Combining multiple requests that arrive within a short time window
 * - Reusing cached results across components
 * - Optimizing batch sizes to match Google Maps API limits
 */

import { Coordinates } from "./types";
import logger from "./utils/logger";

interface PendingRequest {
  origin: Coordinates;
  destinations: Coordinates[];
  mode: "driving" | "walking" | "bicycling" | "transit";
  resolve: (distances: number[]) => void;
  reject: (error: Error) => void;
  timestamp: number;
}

class DistanceRequestBatcher {
  private pendingRequests: PendingRequest[] = [];
  private batchTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly BATCH_DELAY = 50; // Wait 50ms to collect more requests (reduced for faster response)
  private readonly MAX_BATCH_SIZE = 25; // Google Maps API limit per request
  private readonly MAX_WAIT_TIME = 200; // Maximum wait time before processing (ms) (reduced for faster response)
  private isProcessing = false;

  /**
   * Queue a distance calculation request for batching
   */
  async queueRequest(
    origin: Coordinates,
    destinations: Coordinates[],
    mode: "driving" | "walking" | "bicycling" | "transit" = "driving",
  ): Promise<number[]> {
    return new Promise((resolve, reject) => {
      this.pendingRequests.push({
        origin,
        destinations,
        mode,
        resolve,
        reject,
        timestamp: Date.now(),
      });

      // If we have enough destinations to fill a batch, process immediately
      const totalDestinations = this.pendingRequests.reduce(
        (sum, req) => sum + req.destinations.length,
        0,
      );
      if (totalDestinations >= this.MAX_BATCH_SIZE) {
        this.processBatch();
        return;
      }

      // Check if oldest request has been waiting too long
      const oldestRequest = this.pendingRequests[0];
      if (
        oldestRequest &&
        Date.now() - oldestRequest.timestamp > this.MAX_WAIT_TIME
      ) {
        this.processBatch();
        return;
      }

      // Set timeout to process batch after delay
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout);
      }

      this.batchTimeout = setTimeout(() => {
        this.processBatch();
      }, this.BATCH_DELAY);
    });
  }

  /**
   * Process the current batch of requests
   */
  private async processBatch(): Promise<void> {
    if (this.isProcessing || this.pendingRequests.length === 0) {
      return;
    }

    this.isProcessing = true;

    // Log for debugging
    const totalDestinations = this.pendingRequests.reduce(
      (sum, req) => sum + req.destinations.length,
      0,
    );
    logger.log(
      `[DISTANCE_BATCHER] Processing batch: ${this.pendingRequests.length} requests, ${totalDestinations} total destinations`,
    );

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    // Group requests by origin and mode (same origin + mode can be batched together)
    const requestsByOriginAndMode = new Map<
      string,
      {
        origin: Coordinates;
        mode: "driving" | "walking" | "bicycling" | "transit";
        requests: PendingRequest[];
      }
    >();

    const requestsToProcess = [...this.pendingRequests];
    this.pendingRequests = [];

    // Group requests by origin+mode key
    requestsToProcess.forEach((req) => {
      const key = `${req.origin.lat},${req.origin.lng}_${req.mode}`;
      if (!requestsByOriginAndMode.has(key)) {
        requestsByOriginAndMode.set(key, {
          origin: req.origin,
          mode: req.mode,
          requests: [],
        });
      }
      requestsByOriginAndMode.get(key)!.requests.push(req);
    });

    // Process each group
    const processPromises: Promise<void>[] = [];

    requestsByOriginAndMode.forEach(async (group) => {
      // Combine all destinations from requests with same origin+mode
      const allDestinations: Coordinates[] = [];
      const destinationToRequestMap = new Map<
        number,
        { requestIndex: number; destinationIndex: number }
      >();

      group.requests.forEach((req, reqIndex) => {
        req.destinations.forEach((dest, destIndex) => {
          const globalIndex = allDestinations.length;
          allDestinations.push(dest);
          destinationToRequestMap.set(globalIndex, {
            requestIndex: reqIndex,
            destinationIndex: destIndex,
          });
        });
      });

      // Split into chunks if exceeds API limit (25 destinations per request)
      const chunks: Coordinates[][] = [];
      for (let i = 0; i < allDestinations.length; i += this.MAX_BATCH_SIZE) {
        chunks.push(allDestinations.slice(i, i + this.MAX_BATCH_SIZE));
      }

      // Process chunks sequentially to avoid overwhelming the API
      // This ensures we don't make 50+ parallel requests
      const chunkResults: number[][] = [];
      for (const chunk of chunks) {
        try {
          // Validate chunk before making request
          if (!chunk || chunk.length === 0) {
            console.warn("[DISTANCE_BATCHER] Empty chunk, skipping");
            continue;
          }

          const requestBody = {
            origins: [`${group.origin.lat},${group.origin.lng}`],
            destinations: chunk.map((dest) => `${dest.lat},${dest.lng}`),
            mode: group.mode,
          };

          // Validate request body
          if (
            !requestBody.origins ||
            requestBody.origins.length === 0 ||
            !requestBody.destinations ||
            requestBody.destinations.length === 0
          ) {
            console.error(
              "[DISTANCE_BATCHER] Invalid request body:",
              requestBody,
            );
            throw new Error("Invalid request body for distance matrix");
          }

          const response = await fetch("/api/maps/distance-matrix", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            const errorText = await response
              .text()
              .catch(() => "Unknown error");
            console.error(
              `[DISTANCE_BATCHER] API error ${response.status}:`,
              errorText,
            );
            throw new Error(
              `Failed to calculate distance matrix: ${response.status} ${errorText}`,
            );
          }

          let data;
          try {
            const responseText = await response.text();
            if (!responseText || responseText.trim().length === 0) {
              throw new Error("Empty response from distance matrix API");
            }
            data = JSON.parse(responseText);
          } catch (parseError) {
            console.error(
              "[DISTANCE_BATCHER] Failed to parse API response:",
              parseError,
            );
            throw new Error("Invalid JSON response from distance matrix API");
          }
          const distances: number[] = [];

          // The API returns results in the format: { results: [{ distance: { value: number }, status: string }], status: string }
          // Results are ordered by destination index
          if (data.results && data.results.length > 0) {
            data.results.forEach(
              (
                result: { distance?: { value?: number }; status?: string },
                index: number,
              ) => {
                if (result.status === "OK" && result.distance?.value) {
                  distances.push(result.distance.value / 1000); // Convert to km
                } else {
                  // If status is not OK, calculate Haversine distance as fallback
                  if (index < chunk.length) {
                    const dest = chunk[index];
                    // Use Haversine formula as fallback
                    const R = 6371; // Earth radius in km
                    const dLat = this.deg2rad(dest.lat - group.origin.lat);
                    const dLng = this.deg2rad(dest.lng - group.origin.lng);
                    const a =
                      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                      Math.cos(this.deg2rad(group.origin.lat)) *
                        Math.cos(this.deg2rad(dest.lat)) *
                        Math.sin(dLng / 2) *
                        Math.sin(dLng / 2);
                    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                    distances.push(R * c);
                  } else {
                    distances.push(0);
                  }
                }
              },
            );
          } else {
            // If no results, calculate Haversine for all destinations
            chunk.forEach((dest) => {
              const R = 6371; // Earth radius in km
              const dLat = this.deg2rad(dest.lat - group.origin.lat);
              const dLng = this.deg2rad(dest.lng - group.origin.lng);
              const a =
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(this.deg2rad(group.origin.lat)) *
                  Math.cos(this.deg2rad(dest.lat)) *
                  Math.sin(dLng / 2) *
                  Math.sin(dLng / 2);
              const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
              distances.push(R * c);
            });
          }

          chunkResults.push(distances);
        } catch (error) {
          // Reject all requests in this group
          group.requests.forEach((req) => {
            req.reject(
              error instanceof Error ? error : new Error(String(error)),
            );
          });
          return;
        }
      }

      // Map results back to individual requests
      const allDistances = chunkResults.flat();
      let globalIndex = 0;

      group.requests.forEach((req) => {
        const requestDistances: number[] = [];
        for (let i = 0; i < req.destinations.length; i++) {
          if (globalIndex < allDistances.length) {
            requestDistances.push(allDistances[globalIndex]);
            globalIndex++;
          } else {
            requestDistances.push(0);
          }
        }
        req.resolve(requestDistances);
      });
    });

    await Promise.all(processPromises);
    this.isProcessing = false;

    // Process any new requests that arrived while processing
    if (this.pendingRequests.length > 0) {
      setTimeout(() => this.processBatch(), this.BATCH_DELAY);
    }
  }

  /**
   * Clear the queue (useful for cleanup)
   */
  clearQueue(): void {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
    // Reject all pending requests
    this.pendingRequests.forEach((req) => {
      req.reject(new Error("Request queue cleared"));
    });
    this.pendingRequests = [];
    this.isProcessing = false;
  }

  /**
   * Get current queue size (for debugging)
   */
  getQueueSize(): number {
    return this.pendingRequests.length;
  }

  /**
   * Convert degrees to radians
   */
  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }
}

// Export singleton instance for client-side use
// This ensures all components share the same batching queue
export const distanceRequestBatcher = new DistanceRequestBatcher();
